import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
    Container, Button, Row, Col, Input, Label, Card, CardBody, Badge
} from 'reactstrap';

import { Calendar, momentLocalizer, SlotInfo } from 'react-big-calendar';
import withDragAndDrop, { EventInteractionArgs } from 'react-big-calendar/lib/addons/dragAndDrop';
import moment from 'moment';
import 'moment/locale/pt-br';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import Select from 'react-select';
import type { View } from 'react-big-calendar';

import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import Header from '../../components/Headers/Header';
import AdminNavbar from '../../components/Navbars/AdminNavbar/AdminNavbar';
import { AuthContext } from '../../context/AuthContext';
import { AgendaContext } from '../../context/AgendaContext';
import { AgendaType, CreateAgendaType, EditAgendaType } from '../../context/hooks/useAgenda';
import { TipoAgendaContext } from '../../context/TipoAgendaContext';
import { TerceiroContext } from '../../context/TerceiroContext';
import useUser from '../../context/hooks/useUser';
import api from '../../api';

import { CreateAgendaModal } from '../../components/Agenda/CreateAgendaModal';
import { EditAgendaGerenteModal } from '../../components/Agenda/EditAgendaGerenteModal';

import './AgendaGerente.css';

moment.locale('pt-br');
const localizer = momentLocalizer(moment);
const DnDCalendar = withDragAndDrop(Calendar);

const messages = {
    allDay: 'Dia inteiro',
    previous: 'Anterior',
    next: 'Próximo',
    today: 'Hoje',
    month: 'Mês',
    week: 'Semana',
    day: 'Dia',
    agenda: 'Agenda',
    date: 'Data',
    time: 'Hora',
    event: 'Evento',
    noEventsInRange: 'Nenhum evento nesse período.',
    showMore: (total: number) => `+ Ver mais (${total})`
};

const AgendaGerente: React.FC = () => {
    const { objUserTk } = useContext(AuthContext);
    const {
        getAgendaPaged,
        updateAgenda,
        listAgenda,
        downloadAgendaPdfByDate,
        downloadAgendaPdfCustom,
        printAgendaPdfByDate,
        printAgendaPdfCustom,
        listAllAgenda
    } = useContext(AgendaContext);

    const { getAllTipoAgendaPaged } = useContext(TipoAgendaContext);
    const { getPermissionsByUser } = useUser();
    const { getTerceiroPaged, handleGetTerceiroByAgendaId } = useContext(TerceiroContext);

    const [loaging, setLoading] = useState(true);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editingAgenda, setEditingAgenda] = useState<EditAgendaType | null>(null);

    const [selectedDate, setSelectedDate] = useState<string>('');
    const [customStartDate, setCustomStartDate] = useState<string>('');
    const [customEndDate, setCustomEndDate] = useState<string>('');
    const [selectedAgendaIds, setSelectedAgendaIds] = useState<number[]>([]);

    const [currentView, setCurrentView] = useState<View>('month');
    const today = new Date();
    const agendaBaseDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 365);

    const empresaId: number | undefined = useMemo(
        () => (objUserTk as any)?.EmpresaId,
        [objUserTk]
    );

    const monthStart = useMemo(() => new Date(today.getFullYear(), today.getMonth(), 1), [today]);
    const monthEnd = useMemo(() => new Date(today.getFullYear(), today.getMonth() + 1, 0), [today]);

    const [drawerOpen, setDrawerOpen] = useState<boolean>(true);
    const [rejStartDate, setRejStartDate] = useState<string>(monthStart.toISOString().slice(0, 10));
    const [rejEndDate, setRejEndDate] = useState<string>(monthEnd.toISOString().slice(0, 10));
    const [rejPageNumber, setRejPageNumber] = useState<number>(1);
    const [rejPageSize, setRejPageSize] = useState<number>(10);
    const [rejPageCount, setRejPageCount] = useState<number>(0);
    const [rejectedAgendas, setRejectedAgendas] = useState<AgendaType[]>([]);
    const [loadingRejected, setLoadingRejected] = useState<boolean>(false);

    const fetchRejected = async (page = rejPageNumber) => {
        if (!empresaId) return;
        setLoadingRejected(true);
        try {
            const qs = new URLSearchParams();
            qs.append('pageNumber', String(page));
            qs.append('pageSize', String(rejPageSize));
            qs.append('empresaId', String(empresaId));
            if (rejStartDate) qs.append('startDate', rejStartDate);
            if (rejEndDate) qs.append('endDate', rejEndDate);

            const { data } = await api.get(`/Agenda/com-rejeicao?${qs.toString()}`);
            setRejectedAgendas(data?.result ?? []);
            setRejPageCount(data?.pageCount ?? 0);
            setRejPageNumber(page);
        } catch (e) {
            console.error('Erro ao buscar agendas rejeitadas:', e);
        } finally {
            setLoadingRejected(false);
        }
    };

    useEffect(() => {
        setLoading(true);
        getAllTipoAgendaPaged(1, 1000, empresaId || 0);
        getTerceiroPaged(1, '', empresaId || 0, 1000, 'Fotógrafo');
        listAllAgenda(empresaId || 0)
            .then(() => setLoading(false))
            .catch(() => setLoading(false));
    }, [empresaId]);

    useEffect(() => {
        const bootstrapPerms = async () => {
            if (objUserTk?.UserId) await getPermissionsByUser(objUserTk.UserId);
        };
        bootstrapPerms().catch(console.error);
    }, [objUserTk?.UserId]);

    useEffect(() => {
        if (drawerOpen) fetchRejected(1);
    }, [drawerOpen, empresaId]);

    const [newAgenda, setNewAgenda] = useState<CreateAgendaType>({
        description: '',
        location: '',
        responsible: '',
        startDate: '',
        endDate: '',
        beca: '',
        cursoId: 0,
        tipoAgendaId: 0,
        empresaId: empresaId || 0,
        contratoId: null,
        terceiros: [],
    });

    const handleEditAgenda = (agenda: AgendaType) => {
        const updateData: EditAgendaType = {
            id: agenda.id,
            description: agenda.description,
            location: agenda.location,
            responsible: agenda.responsible,
            startDate: agenda.startDate,
            endDate: agenda.endDate,
            beca: agenda.beca,
            cursoId: agenda.cursoId || 0,
            cursoName: agenda.cursoName || "",
            cursoIds: agenda.cursoIds || [],
            cursoNames: agenda.cursoNames || [],
            step: agenda.step,
            tipoAgendaId: agenda.tipoAgendaId,
        };
        setEditingAgenda(updateData);
        handleGetTerceiroByAgendaId(agenda.id);
        setEditModalOpen(true);
    };

    const handleSelectSlot = (slotInfo: SlotInfo) => {
        setNewAgenda({
            ...newAgenda,
            startDate: moment(slotInfo.start).format('YYYY-MM-DDTHH:mm'),
            endDate: moment(slotInfo.end).format('YYYY-MM-DDTHH:mm'),
        });
        setCreateModalOpen(true);
    };

    const handleSelectEvent = (event: any, e: React.SyntheticEvent<HTMLElement>) => {
        e.preventDefault();
        const agenda = listAgenda.find(a => a.id === event.id);
        if (agenda) handleEditAgenda(agenda);
    };

    const handleEventDrop = (args: EventInteractionArgs<any>) => {
        const { event, start, end } = args;
        const updatedAgenda = listAgenda.find(a => a.id === event.id);
        if (updatedAgenda) {
            updateAgenda({
                ...updatedAgenda,
                startDate: moment(start).format('YYYY-MM-DDTHH:mm'),
                endDate: moment(end).format('YYYY-MM-DDTHH:mm'),
            }).then(() => getAgendaPaged(1, empresaId, 9));
        }
    };

    const events = listAgenda.map((agenda: AgendaType) => ({
        id: agenda.id,
        title: agenda.description,
        start: new Date(agenda.startDate),
        end: new Date(agenda.endDate),
        cursoName: agenda.cursoName ?? '-',
    }));

    const options = listAgenda.map((agenda) => ({
        value: agenda.id,
        label: `${agenda.id} - ${agenda.description}`,
    }));

    const handleMultiSelectChange = (selectedOptions: any) => {
        const selectedIds = selectedOptions ? selectedOptions.map((opt: any) => opt.value) : [];
        setSelectedAgendaIds(selectedIds);
    };

    const formatDateTime = (iso: string) => {
        try {
            const d = new Date(iso);
            return d.toLocaleString(undefined, {
                day: '2-digit', month: '2-digit', year: 'numeric',
                hour: '2-digit', minute: '2-digit'
            });
        } catch { return iso; }
    };

    return (
        <>
            <LoadingSpinner />
            <Header />
            <AdminNavbar brandText={"Gerenciar Agenda"} search={false} titleSearch={""} />

            <Container className="mt-5">
                <Row className="align-items-center mb-4">
                    <Col xs="12" md="2" lg="2">
                        <Label className="form-control-label">Data inicial</Label>
                        <Input
                            className="form-control-alternative"
                            type="date"
                            value={customStartDate}
                            onChange={(e) => setCustomStartDate(e.target.value)}
                        />
                    </Col>
                    <Col xs="12" md="2" lg="2">
                        <Label className="form-control-label">Data final</Label>
                        <Input
                            className="form-control-alternative"
                            type="date"
                            value={customEndDate}
                            onChange={(e) => setCustomEndDate(e.target.value)}
                        />
                    </Col>
                    <Col xs="12" md="4" lg="4">
                        <Label className="form-control-label">Selecionar eventos específicos</Label>
                        <Select
                            className="form-control-alternative"
                            isMulti
                            name="agendas"
                            options={options}
                            classNamePrefix="select"
                            onChange={handleMultiSelectChange}
                            value={options.filter(opt => selectedAgendaIds.includes(opt.value))}
                            placeholder="Digite para buscar eventos..."
                            styles={{
                                menu: (provided) => ({ ...provided, zIndex: 9999 }),
                                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            }}
                            menuPortalTarget={document.body}
                        />
                    </Col>
                    <Col xs="12" md="4" lg="4" className="d-flex justify-content-start justify-content-md-end mt-3 mt-md-0">
                        <Button
                            style={{ color: "#FFF", backgroundColor: "#585858" }}
                            onClick={() => {
                                const filtrosVazios = !customStartDate && !customEndDate && (!selectedAgendaIds || selectedAgendaIds.length === 0);
                                printAgendaPdfCustom({
                                    empresaId: empresaId || 0,
                                    startDate: filtrosVazios ? undefined : (customStartDate || undefined),
                                    endDate: filtrosVazios ? undefined : (customEndDate || undefined),
                                    agendaIds: filtrosVazios ? undefined : (selectedAgendaIds.length > 0 ? selectedAgendaIds : undefined),
                                });
                            }}
                        >
                            Imprimir PDF por Período/Eventos
                        </Button>
                        <Button
                            className="ml-2"
                            style={{ color: "#FFF", backgroundColor: "#585858" }}
                            onClick={() => {
                                const filtrosVazios = !customStartDate && !customEndDate && (!selectedAgendaIds || selectedAgendaIds.length === 0);
                                downloadAgendaPdfCustom({
                                    empresaId: empresaId || 0,
                                    startDate: filtrosVazios ? undefined : (customStartDate || undefined),
                                    endDate: filtrosVazios ? undefined : (customEndDate || undefined),
                                    agendaIds: filtrosVazios ? undefined : (selectedAgendaIds.length > 0 ? selectedAgendaIds : undefined),
                                });
                            }}
                        >
                            Baixar PDF por Período/Eventos
                        </Button>
                    </Col>
                </Row>

                <Row className="g-3">
                    <Col md="12">
                        <Card className="shadow-sm border-0">
                            <CardBody>
                                <DnDCalendar
                                    localizer={localizer}
                                    events={events}
                                    startAccessor={(event: any) => new Date(event.start)}
                                    endAccessor={(event: any) => new Date(event.end)}
                                    selectable
                                    onSelectSlot={handleSelectSlot}
                                    onSelectEvent={handleSelectEvent}
                                    onEventDrop={handleEventDrop}
                                    resizable
                                    messages={messages}
                                    view={currentView}
                                    onView={(view) => setCurrentView(view)}
                                    views={{ month: true, week: true, day: true, agenda: true }}
                                    date={currentView === 'agenda' ? agendaBaseDate : undefined}
                                    length={730}
                                    formats={{
                                        agendaDateFormat: (date) => moment(date).format('dddd, DD [de] MMMM [de] YYYY'),
                                        agendaTimeFormat: 'HH:mm',
                                        timeGutterFormat: 'HH:mm',
                                    }}
                                    style={{ height: 520 }}
                                    components={{
                                        agenda: {
                                            event: (props: any) => (
                                                <div>
                                                    <div><strong>{props.event.title}</strong></div>
                                                    <div><small><strong>Curso:</strong> {props.event.cursoName}</small></div>
                                                </div>
                                            )
                                        }
                                    }}
                                />
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                <div className="rejected-wrapper mt-4">
                    <Card className="shadow-sm border-0">
                        <CardBody>
                            <div className="drawer-filters drawer-filters--inline">
                                <div className="drawer-item">
                                    <Label className="form-control-label mb-1">Início</Label>
                                    <Input className="form-control-alternative" type="date" value={rejStartDate} onChange={(e) => setRejStartDate(e.target.value)} />
                                </div>
                                <div className="drawer-item">
                                    <Label className="form-control-label mb-1">Fim</Label>
                                    <Input className="form-control-alternative" type="date" value={rejEndDate} onChange={(e) => setRejEndDate(e.target.value)} />
                                </div>
                                <div className="drawer-item">
                                    <Label className="form-control-label mb-1">Itens/página</Label>
                                    <Input
                                        className="form-control-alternative"
                                        type="select"
                                        value={rejPageSize}
                                        onChange={(e) => { setRejPageSize(parseInt(e.target.value || '10', 10)); fetchRejected(1); }}
                                    >
                                        <option value={6}>6</option>
                                        <option value={10}>10</option>
                                        <option value={15}>15</option>
                                        <option value={25}>25</option>
                                    </Input>
                                </div>
                                <div className="drawer-actions">
                                    <Button style={{ background: '#585858', color: '#fff' }} onClick={() => fetchRejected(1)} disabled={loadingRejected}>
                                        {loadingRejected ? 'Carregando…' : 'Aplicar'}
                                    </Button>
                                </div>
                            </div>

                            {rejectedAgendas.length === 0 && !loadingRejected ? (
                                <div className="text-center text-muted py-4">Nenhum evento rejeitado no período.</div>
                            ) : (
                                <div className="drawer-list drawer-list--below">
                                    {rejectedAgendas.map((a) => {
                                        const isPendente = a.step === 1;
                                        return (
                                            <div
                                                key={a.id}
                                                className={`drawer-card ${isPendente ? 'clickable' : ''}`}
                                                onClick={() => isPendente && handleEditAgenda(a)}
                                            >
                                                <div className="d-flex justify-content-between">
                                                    <h6 className="mb-1">{a.description || 'Sem descrição'}</h6>
                                                    <div className="d-flex gap-2">
                                                        <Badge color="danger" pill>REJEITADA</Badge>
                                                        <Badge color={isPendente ? 'warning' : 'success'} pill>
                                                            {isPendente ? 'PENDENTE' : 'OK'}
                                                        </Badge>
                                                    </div>
                                                </div>
                                                <div className="small text-muted mb-1">{a.location || 'Local não informado'}</div>
                                                <div className="small">
                                                    <span className="me-2"><strong>Início:</strong> {formatDateTime(a.startDate)}</span><br />
                                                    <span><strong>Fim:</strong> {formatDateTime(a.endDate)}</span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {rejPageCount > 1 && (
                                <div className="d-flex align-items-center justify-content-center gap-2 mt-3">
                                    <Button
                                        color="secondary"
                                        size="sm"
                                        disabled={rejPageNumber <= 1}
                                        onClick={() => fetchRejected(rejPageNumber - 1)}
                                    >
                                        ‹ Anterior
                                    </Button>
                                    <span className="mx-2 small">
                                        Página <strong>{rejPageNumber}</strong> de <strong>{rejPageCount}</strong>
                                    </span>
                                    <Button
                                        color="secondary"
                                        size="sm"
                                        disabled={rejPageNumber >= rejPageCount}
                                        onClick={() => fetchRejected(rejPageNumber + 1)}
                                    >
                                        Próxima ›
                                    </Button>
                                </div>
                            )}
                        </CardBody>
                    </Card>
                </div>
            </Container>

            <CreateAgendaModal
                isOpen={createModalOpen}
                toggle={() => setCreateModalOpen(false)}
                defaultStart={newAgenda.startDate}
                defaultEnd={newAgenda.endDate}
                onCreated={() => listAllAgenda(empresaId || 0)}
            />

            <EditAgendaGerenteModal
                isOpen={editModalOpen}
                toggle={() => setEditModalOpen(false)}
                agenda={editingAgenda}
                onUpdated={() => listAllAgenda(empresaId || 0)}
                onDeleted={() => listAllAgenda(empresaId || 0)}
            />
        </>
    );
};

export default AgendaGerente;